import { FIT_NOT_WELL, FIT_WELL, NARRATIVE, SCENARIOS, SOLUTIONS_INTRO } from '@/content/solutions';
import { ActionRow } from '@/components/ActionButton';
import { Card, CheckList } from '@/components/Card';
import { Icon } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { PageHero, Section, SectionHeading } from '@/components/Section';
import { SolutionsShowcase } from '@/components/SolutionsShowcase';
import { Seo } from '@/components/Seo';

export function SolutionsPage() {
  return (
    <>
      <Seo
        path="/solutions"
        crumbs={[
          { name: '首页', path: '/' },
          { name: '解决方案', path: '/solutions' },
        ]}
      />

      <PageHero
        eyebrow={SOLUTIONS_INTRO.eyebrow}
        title={SOLUTIONS_INTRO.title}
        lead={SOLUTIONS_INTRO.lead}
        actions={<ActionRow actions={SOLUTIONS_INTRO.actions} />}
      />

      <SolutionsShowcase />

      <Section>
        <nav aria-label="场景索引" className="mb-10 flex flex-wrap gap-2">
          {SCENARIOS.map((scenario) => (
            <a
              key={scenario.id}
              href={`#${scenario.id}`}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-surface-1 px-3.5 py-2 text-xs text-fg-muted transition-colors hover:border-brand hover:text-brand"
            >
              <Icon name={scenario.icon} size={14} />
              {scenario.title}
            </a>
          ))}
        </nav>

        <div className="flex flex-col gap-8">
          {SCENARIOS.map((scenario, index) => (
            <Reveal key={scenario.id} delay={index}>
              <Card id={scenario.id}>
                <div className="grid grid-cols-[minmax(0,1fr)] gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-grad-tint text-brand">
                        <Icon name={scenario.icon} size={21} />
                      </span>
                      <div>
                        <h2 className="text-lg font-semibold text-fg">{scenario.title}</h2>
                        <p className="mt-0.5 text-xs text-fg-muted">{scenario.audience}</p>
                      </div>
                    </div>
                    <p className="text-base font-medium text-fg">{scenario.tagline}</p>
                    <p className="text-sm leading-relaxed text-fg-muted">{scenario.situation}</p>
                    <div className="mt-auto flex flex-wrap gap-2 pt-2">
                      {scenario.outcomes.map((outcome) => (
                        <span
                          key={outcome}
                          className="inline-flex items-center gap-1.5 rounded-md bg-grad-tint px-3 py-1.5 text-xs font-medium text-brand"
                        >
                          <Icon name="check" size={13} className="text-fg-subtle" />
                          {outcome}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-xl border border-line bg-surface-1 bg-grad-soft p-5">
                    <span className="label-mono text-brand">平台怎么接住</span>
                    <CheckList items={scenario.approach} className="mt-3" />
                  </div>
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="raised" id="fit">
        <SectionHeading
          eyebrow="适配判断"
          title="何时适用，何时不适用"
          lead="我们也列出不适用的情形，以省去双方不必要的往返。"
        />

        <div className="mt-10 grid gap-5 md:grid-cols-2">
          <Card className="flex flex-col gap-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
              <Icon name="check" size={16} className="text-fg-muted" />
              适合
            </h3>
            <CheckList items={FIT_WELL} />
          </Card>

          <Card className="flex flex-col gap-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-fg">
              <Icon name="close" size={16} className="text-fg-muted" />
              不适合
            </h3>
            <ul className="space-y-2.5">
              {FIT_NOT_WELL.map((item) => (
                <li key={item} className="flex gap-2.5 text-sm leading-relaxed text-fg-muted">
                  <Icon name="close" size={15} className="mt-0.5 text-fg-subtle" />
                  {item}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      <Section tone="soft">
        <div className="max-w-3xl">
          <span className="label-mono text-brand">{NARRATIVE.eyebrow}</span>
          <h2 className="mt-3 text-[1.625rem] font-semibold text-fg md:text-[2.5rem]">{NARRATIVE.title}</h2>
          <div className="mt-5 space-y-4 text-[0.9375rem] leading-relaxed text-fg-muted">
            {NARRATIVE.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {NARRATIVE.points.map((point) => (
            <Card key={point.title} className="flex flex-col gap-2">
              <Icon name={point.icon} size={18} className="text-brand" />
              <h3 className="text-sm font-semibold text-fg">{point.title}</h3>
              <p className="text-xs leading-relaxed text-fg-muted">{point.text}</p>
            </Card>
          ))}
        </div>
      </Section>
    </>
  );
}
